import type { FieldGroup, ChipColor } from './EntityProperties'

const KYC_STATUSES_COLOR = (v: string): ChipColor => {
  switch (v) {
    case 'VERIFIED':
      return 'success'
    case 'PENDING':
      return 'info'
    case 'REJECTED':
      return 'error'
    case 'EXPIRED':
    case 'SUSPENDED':
      return 'warning'
    default:
      return 'default'
  }
}

const ACCOUNT_STATUS_COLOR = (v: string): ChipColor => {
  switch (v) {
    case 'ACTIVE':
      return 'success'
    case 'INACTIVE':
    case 'SUSPENDED':
    case 'DEACTIVATED':
      return 'error'
    case 'PENDING_PROFILE':
    case 'PENDING':
    case 'PENDING_APPROVAL':
      return 'info'
    default:
      return 'default'
  }
}

const USER_STATUS_COLOR = (v: string): ChipColor => {
  switch (v) {
    case 'ACTIVE':
      return 'success'
    case 'DEACTIVATED':
      return 'error'
    case 'PENDING_APPROVAL':
      return 'warning'
    default:
      return 'default'
  }
}

export const accountSchema: FieldGroup[] = [
  {
    title: 'Identity',
    columns: 2,
    fields: [
      { key: 'id', label: 'Account ID', kind: { kind: 'id' }, readOnly: true, full: true },
      { key: 'name', label: 'Display Name', kind: { kind: 'text' } },
      { key: 'organizationCode', label: 'Organization Code', kind: { kind: 'text' } },
      {
        key: 'type',
        label: 'Account Type',
        kind: { kind: 'chip', enumName: 'accountType' },
        readOnly: true,
      },
      {
        key: 'status',
        label: 'Status',
        kind: { kind: 'chip', enumName: 'accountStatus', color: ACCOUNT_STATUS_COLOR },
      },
      { key: 'rating', label: 'Rating', kind: { kind: 'rating' }, readOnly: true },
    ],
  },
  {
    title: 'Business',
    columns: 2,
    fields: [
      { key: 'businessName', label: 'Business Name', kind: { kind: 'text' } },
      { key: 'businessLegalId', label: 'Business Legal ID', kind: { kind: 'text' } },
      { key: 'vatNum', label: 'VAT Number', kind: { kind: 'text' } },
      {
        key: 'businessAddress',
        label: 'Business Address',
        kind: { kind: 'multiline', rows: 2 },
        full: true,
      },
    ],
  },
  {
    title: 'Contact',
    columns: 2,
    fields: [
      { key: 'contactEmail', label: 'Contact Email', kind: { kind: 'email' } },
      { key: 'contactPhone', label: 'Contact Phone', kind: { kind: 'phone' } },
      {
        key: 'billingAddress',
        label: 'Billing Address',
        kind: { kind: 'multiline', rows: 2 },
        full: true,
      },
    ],
  },
  {
    title: 'Billing',
    columns: 3,
    fields: [
      {
        key: 'paymentMethod',
        label: 'Payment Method',
        kind: { kind: 'enum', enumName: 'paymentMethod' },
      },
      {
        key: 'defaultPaymentTerms',
        label: 'Default Payment Terms',
        kind: { kind: 'enum', enumName: 'paymentTerms' },
      },
      {
        key: 'defaultCurrency',
        label: 'Default Currency',
        kind: { kind: 'enum', enumName: 'currency' },
      },
    ],
  },
  {
    title: 'Subscription',
    columns: 2,
    fields: [
      { key: 'subscriptionStartDate', label: 'Subscription Start', kind: { kind: 'date' } },
      { key: 'subscriptionEndDate', label: 'Subscription End', kind: { kind: 'date' } },
    ],
  },
  {
    title: 'System',
    columns: 2,
    fields: [
      { key: 'createdAt', label: 'Created', kind: { kind: 'datetime' }, readOnly: true },
      { key: 'updatedAt', label: 'Updated', kind: { kind: 'datetime' }, readOnly: true },
    ],
  },
]

// ---------------------------------------------------------------------------

export const vehicleSchema: FieldGroup[] = [
  {
    title: 'Identity',
    columns: 2,
    fields: [
      { key: 'id', label: 'Vehicle ID', kind: { kind: 'id' }, readOnly: true, full: true },
      { key: 'accountId', label: 'Account ID', kind: { kind: 'id' }, readOnly: true },
      { key: 'defaultDriverId', label: 'Default Driver ID', kind: { kind: 'id' }, readOnly: true },
      { key: 'plateNo', label: 'Plate Number', kind: { kind: 'text' } },
    ],
  },
  {
    title: 'Specs',
    columns: 3,
    fields: [
      { key: 'maker', label: 'Maker', kind: { kind: 'text' } },
      { key: 'model', label: 'Model', kind: { kind: 'text' } },
      { key: 'year', label: 'Year', kind: { kind: 'number', min: 1950, max: 2100 } },
      {
        key: 'type',
        label: 'Type',
        kind: { kind: 'enum', enumName: 'vehicleType' },
      },
      { key: 'capacityKg', label: 'Capacity', kind: { kind: 'number', min: 0, unit: 'kg' } },
      {
        key: 'fuelConsumptionPer100Km',
        label: 'Fuel',
        kind: { kind: 'number', min: 0, step: 0.1, unit: 'L/100km' },
      },
    ],
  },
  {
    title: 'Dimensions',
    columns: 3,
    fields: [
      { key: 'lengthM', label: 'Length', kind: { kind: 'number', min: 0, step: 0.1, unit: 'm' } },
      { key: 'widthM', label: 'Width', kind: { kind: 'number', min: 0, step: 0.1, unit: 'm' } },
      { key: 'heightM', label: 'Height', kind: { kind: 'number', min: 0, step: 0.1, unit: 'm' } },
    ],
  },
  {
    title: 'Capabilities',
    columns: 3,
    fields: [
      { key: 'boxTruck', label: 'Box Truck', kind: { kind: 'boolean' } },
      { key: 'curtainsider', label: 'Curtainsider', kind: { kind: 'boolean' } },
      { key: 'flatbed', label: 'Flatbed', kind: { kind: 'boolean' } },
      { key: 'hasHazmat', label: 'Hazmat', kind: { kind: 'boolean' } },
      { key: 'hasRefrigeration', label: 'Refrigeration', kind: { kind: 'boolean' } },
      { key: 'hasRamp', label: 'Ramp', kind: { kind: 'boolean' } },
      {
        key: 'loadingSide',
        label: 'Loading Side',
        kind: { kind: 'enum', enumName: 'loadingSide' },
        full: true,
      },
    ],
  },
  {
    title: 'Home Base',
    columns: 1,
    fields: [
      { key: 'homeBaseAddress', label: 'Home Base Address', kind: { kind: 'text' }, full: true },
    ],
  },
  {
    title: 'Compliance',
    columns: 2,
    fields: [
      { key: 'licenseExpiry', label: 'License Expiry', kind: { kind: 'date' } },
      { key: 'lastTestDate', label: 'Last Test Date', kind: { kind: 'date' } },
      {
        key: 'kycVerificationStatus',
        label: 'KYC Status',
        kind: {
          kind: 'chip',
          enumName: 'kycVerificationStatus',
          color: KYC_STATUSES_COLOR,
        },
        readOnly: true,
      },
      { key: 'kycVerifiedAt', label: 'KYC Verified At', kind: { kind: 'datetime' }, readOnly: true },
    ],
  },
  {
    title: 'Performance',
    columns: 2,
    fields: [
      { key: 'rating', label: 'Rating', kind: { kind: 'rating' }, readOnly: true },
      { key: 'ratingCount', label: 'Rating Count', kind: { kind: 'number' }, readOnly: true },
    ],
  },
  {
    title: 'System',
    columns: 2,
    fields: [
      { key: 'createdAt', label: 'Created', kind: { kind: 'datetime' }, readOnly: true },
      { key: 'updatedAt', label: 'Updated', kind: { kind: 'datetime' }, readOnly: true },
    ],
  },
]

// ---------------------------------------------------------------------------

export const driverSchema: FieldGroup[] = [
  {
    title: 'Identity',
    columns: 2,
    fields: [
      { key: 'id', label: 'Driver ID', kind: { kind: 'id' }, readOnly: true, full: true },
      { key: 'accountId', label: 'Account ID', kind: { kind: 'id' }, readOnly: true, full: true },
    ],
  },
  {
    title: 'License',
    columns: 2,
    fields: [
      { key: 'licenseNo', label: 'License No', kind: { kind: 'text' } },
      { key: 'licenseExpiry', label: 'License Expiry', kind: { kind: 'date' } },
    ],
  },
  {
    title: 'User',
    columns: 2,
    fields: [
      { key: 'user.id', label: 'User ID', kind: { kind: 'id' }, readOnly: true, full: true },
      { key: 'user.name', label: 'Name', kind: { kind: 'text' } },
      { key: 'user.nickName', label: 'Nickname', kind: { kind: 'text' } },
      { key: 'user.email', label: 'Email', kind: { kind: 'email' } },
      { key: 'user.phone', label: 'Phone', kind: { kind: 'phone' } },
      {
        key: 'user.status',
        label: 'User Status',
        kind: { kind: 'chip', enumName: 'userStatus', color: USER_STATUS_COLOR },
      },
      { key: 'user.rating', label: 'Rating', kind: { kind: 'rating' }, readOnly: true },
      { key: 'user.lastLoginAt', label: 'Last Login', kind: { kind: 'datetime' }, readOnly: true },
    ],
  },
  {
    title: 'KYC',
    columns: 2,
    fields: [
      {
        key: 'kycVerificationStatus',
        label: 'KYC Status',
        kind: {
          kind: 'chip',
          enumName: 'kycVerificationStatus',
          color: KYC_STATUSES_COLOR,
        },
        readOnly: true,
      },
      { key: 'kycVerifiedAt', label: 'KYC Verified At', kind: { kind: 'datetime' }, readOnly: true },
    ],
  },
  {
    title: 'System',
    columns: 2,
    fields: [
      { key: 'createdAt', label: 'Created', kind: { kind: 'datetime' }, readOnly: true },
      { key: 'updatedAt', label: 'Updated', kind: { kind: 'datetime' }, readOnly: true },
    ],
  },
]

// ---------------------------------------------------------------------------

export const userSchema: FieldGroup[] = [
  {
    title: 'Identity',
    columns: 2,
    fields: [
      { key: 'id', label: 'User ID', kind: { kind: 'id' }, readOnly: true, full: true },
      { key: 'accountId', label: 'Account ID', kind: { kind: 'id' }, readOnly: true, full: true },
      { key: 'name', label: 'Name', kind: { kind: 'text' } },
      { key: 'nickName', label: 'Nickname', kind: { kind: 'text' } },
    ],
  },
  {
    title: 'Contact',
    columns: 2,
    fields: [
      { key: 'email', label: 'Email', kind: { kind: 'email' } },
      { key: 'phone', label: 'Phone', kind: { kind: 'phone' } },
    ],
  },
  {
    title: 'Status',
    columns: 2,
    fields: [
      {
        key: 'status',
        label: 'Status',
        kind: { kind: 'chip', enumName: 'userStatus', color: USER_STATUS_COLOR },
      },
      { key: 'rating', label: 'Rating', kind: { kind: 'rating' }, readOnly: true },
    ],
  },
  {
    title: 'Preferences',
    columns: 2,
    fields: [
      { key: 'locale', label: 'Locale', kind: { kind: 'enum', enumName: 'language' } },
      { key: 'timezone', label: 'Timezone', kind: { kind: 'text' } },
    ],
  },
  {
    title: 'Activity',
    columns: 2,
    fields: [
      { key: 'lastLoginAt', label: 'Last Login', kind: { kind: 'datetime' }, readOnly: true },
    ],
  },
]
